import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchOneSchemaToken,
  fetchOneSchemaTokenSuccess,
  selectOneSchemaToken,
  selectOneSchemaTokenLoading,
  selectOneSchemaTokenError,
} from '../../services/dataOnboarding/redux';
import { State as RootState } from '../../app/redux/types';

const ONE_SCHEMA_JWT_KEY = 'oneSchemaJwt';
const ONE_SCHEMA_JWT_EXPIRY_KEY = 'oneSchemaJwtExpiry';

// Template Keys are centralized here, Client ID comes from .env
const ONESCHEMA_TEMPLATE_KEYS = {
  inventory: 'InventoryImport',
  items: 'MaterialItems',
};

interface UseOneSchemaTokenOptions {
  enabled?: boolean;
  forceRefresh?: boolean;
}

export const useOneSchemaToken = (options: UseOneSchemaTokenOptions = {}) => {
  const { enabled = true, forceRefresh = false } = options;
  const dispatch = useDispatch<any>();
  const token = useSelector((state: RootState) => selectOneSchemaToken(state));
  const loading = useSelector((state: RootState) =>
    selectOneSchemaTokenLoading(state)
  );
  const error = useSelector((state: RootState) =>
    selectOneSchemaTokenError(state)
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (forceRefresh) {
      dispatch(fetchOneSchemaToken());
      return;
    }

    const storedToken = localStorage.getItem(ONE_SCHEMA_JWT_KEY);
    const storedExpiryString = localStorage.getItem(ONE_SCHEMA_JWT_EXPIRY_KEY);

    let isTokenValidInStorage = false;

    if (storedToken && storedExpiryString) {
      const expiryTime = parseInt(storedExpiryString, 10);
      if (!isNaN(expiryTime) && new Date().getTime() < expiryTime) {
        isTokenValidInStorage = true;
      }
    }

    if (isTokenValidInStorage && storedToken) {
      if (!token || token !== storedToken) {
        dispatch(fetchOneSchemaTokenSuccess(storedToken));
      }
    } else {
      dispatch(fetchOneSchemaToken());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, forceRefresh, token, enabled]);

  if (!enabled) {
    return {
      token: null,
      loading: false,
      error: null,
      fetchToken: () => {},
      clientId: process.env.REACT_APP_ONESCHEMA_CLIENT_ID || '',
      templateKeys: ONESCHEMA_TEMPLATE_KEYS,
    };
  }

  return {
    token,
    loading,
    error,
    fetchToken: () => dispatch(fetchOneSchemaToken()),
    clientId: process.env.REACT_APP_ONESCHEMA_CLIENT_ID || '',
    templateKeys: ONESCHEMA_TEMPLATE_KEYS,
  };
};
